/*!
 * Ministitch Performance Engine — v2
 * Additive only: does not replace lazysizes, does not touch analytics/tracking,
 * does not hold back content or fonts from real visitors, does not detect or
 * treat testing tools differently from real users.
 *
 * Deliberately NOT included (see review.md / update.md Phase 4 for why):
 *   - Content/CSS/font holding until interaction or timeout (client declined:
 *     real UX cost for any visitor who doesn't interact within the window)
 *   - Script-tiering that requires hand-tagging every <script> with custom
 *     data attributes (nothing in the theme has these; would mean editing
 *     GTM/GA/Meta/GoKwik tags, which is off-limits per client instruction)
 *   - "Predictive preloading" via localStorage nav graphs — this only ever
 *     learns one visitor's own repeat-visit history on one device/browser,
 *     it cannot learn from aggregate site traffic without a server backend,
 *     so it was cut rather than ship something that doesn't do what it claims
 *   - RUM beacon to a backend endpoint — no such endpoint exists on this
 *     store; metrics are exposed locally via PerfEngine.metrics() instead
 */
(function () {
  "use strict";

  // -----------------------------------------------------------------------
  // 0. Delay a fixed set of app-embed scripts (Slide Cart, Ecomsend,
  //    Bitespeed, Lably) until window.load or first interaction. These are
  //    injected automatically by Shopify's content_for_header — no <script>
  //    tag exists in our own files to add defer to, so they're intercepted
  //    here instead. Applies identically to every visitor; no bot detection,
  //    no permanent skip — this only changes WHEN they run, not IF.
  // -----------------------------------------------------------------------
  (function delayAppEmbeds() {
    var PATTERNS = [/slide-cart-/, /ecomsend-/, /bitespeed-/, /product-labels-and-badges-/, /lably\.js/];
    var held = [];
    var released = false;

    function matches(src) {
      return src && PATTERNS.some(function (p) { return p.test(src); });
    }

    function release() {
      if (released) return;
      released = true;
      held.forEach(function (orig) {
        var s = document.createElement("script");
        Array.prototype.forEach.call(orig.attributes, function (a) {
          if (a.name !== "type") s.setAttribute(a.name, a.value);
        });
        if (!orig.src) s.textContent = orig.textContent;
        document.head.appendChild(s);
      });
      held = [];
    }

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        Array.prototype.forEach.call(m.addedNodes, function (node) {
          if (node.nodeType === 1 && node.tagName === "SCRIPT" && matches(node.src) && node.getAttribute("type") !== "javascript/held") {
            var clone = node.cloneNode(true);
            held.push(clone);
            node.parentNode && node.parentNode.removeChild(node);
          }
        });
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    var events = ["scroll", "mousemove", "touchstart", "click", "keydown"];
    function trigger() {
      events.forEach(function (ev) { window.removeEventListener(ev, trigger); });
      release();
    }
    events.forEach(function (ev) { window.addEventListener(ev, trigger, { passive: true, once: true }); });
    window.addEventListener("load", function () {
      setTimeout(release, 1500); // small grace period after load before releasing
    });
    setTimeout(release, 8000); // hard fallback, never withhold indefinitely
  })();

  var params = new URLSearchParams(window.location.search);
  var DEBUG = params.has("perfdebug");

  var PerfEngine = {
    _report: { scripts: [], iframes: [] },
    _metrics: { lcp: null, cls: 0, inp: null, ttfb: null },
  };
  window.PerfEngine = PerfEngine;

  // ---------------------------------------------------------------------
  // 1. Integration / app detector — diagnostic only, no behavior change.
  //    Call PerfEngine.report() in devtools to see what's actually running.
  // ---------------------------------------------------------------------
  var CATEGORY_PATTERNS = [
    { category: "analytics", pattern: /googletagmanager|google-analytics|gtag|clarity\.ms|facebook\.net|fbevents|hotjar|tiktok|pintrk|luckyorange/i },
    { category: "checkout", pattern: /gokwik|paytring|shop\.app/i },
    { category: "reviews", pattern: /judge\.me|yotpo|loox|stamped/i },
    { category: "chat", pattern: /tidio|gorgias|zendesk|intercom|crisp\.chat/i },
    { category: "marketing", pattern: /klaviyo|discountninja|limoniapps|weglot|ecomsend|omnisrc/i },
    { category: "page-builder", pattern: /pagefly/i },
    { category: "cdn-vendor", pattern: /jquery|cdn\.shopify\.com|jsdelivr|cloudflare/i },
  ];

  function categorize(src) {
    for (var i = 0; i < CATEGORY_PATTERNS.length; i++) {
      if (CATEGORY_PATTERNS[i].pattern.test(src)) return CATEGORY_PATTERNS[i].category;
    }
    return "uncategorized";
  }

  function scanIntegrations() {
    var scripts = Array.prototype.slice.call(document.scripts);
    PerfEngine._report.scripts = scripts
      .filter(function (s) { return !!s.src; })
      .map(function (s) {
        return { src: s.src, category: categorize(s.src), async: s.async, defer: s.defer };
      });

    var iframes = Array.prototype.slice.call(document.querySelectorAll("iframe[src]"));
    PerfEngine._report.iframes = iframes.map(function (f) {
      return { src: f.src, category: categorize(f.src) };
    });
  }

  PerfEngine.report = function () {
    scanIntegrations();
    var byCategory = {};
    PerfEngine._report.scripts.forEach(function (s) {
      byCategory[s.category] = byCategory[s.category] || [];
      byCategory[s.category].push(s.src);
    });
    console.table(
      Object.keys(byCategory).map(function (cat) {
        return { category: cat, count: byCategory[cat].length, examples: byCategory[cat].slice(0, 3).join(", ") };
      })
    );
    return PerfEngine._report;
  };

  // ---------------------------------------------------------------------
  // 2. Non-critical script loader — infrastructure only.
  //    Not wired to any existing script (GTM/GA/Meta/GoKwik/Clarity all
  //    untouched). Available for a future, separately-approved app you
  //    want to genuinely defer, e.g.:
  //      PerfEngine.loadScript(url, { tier: 'idle' | 'interaction' | 2000 });
  // ---------------------------------------------------------------------
  PerfEngine.loadScript = function (src, opts) {
    opts = opts || {};
    var attrs = opts.attrs || {};
    var loaded = false;

    function inject() {
      if (loaded) return;
      loaded = true;
      var s = document.createElement("script");
      s.src = src;
      s.async = true;
      Object.keys(attrs).forEach(function (k) { s.setAttribute(k, attrs[k]); });
      document.head.appendChild(s);
      if (DEBUG) console.info("[PerfEngine] loaded (" + opts.tier + "):", src);
    }

    if (opts.tier === "idle") {
      if ("requestIdleCallback" in window) requestIdleCallback(inject, { timeout: 3000 });
      else setTimeout(inject, 1500);
    } else if (opts.tier === "interaction") {
      var events = ["scroll", "mousemove", "touchstart", "keydown"];
      var fire = function () {
        events.forEach(function (ev) { window.removeEventListener(ev, fire); });
        inject();
      };
      events.forEach(function (ev) { window.addEventListener(ev, fire, { passive: true, once: true }); });
      setTimeout(inject, 4000); // hard fallback so it never silently never-loads
    } else if (typeof opts.tier === "number") {
      setTimeout(inject, opts.tier);
    } else {
      inject();
    }
  };

  // ---------------------------------------------------------------------
  // 3. Hover-intent preloading — prefetches a same-origin page's HTML
  //    after a sustained ~0.65s hover. Excludes cart/checkout/account and
  //    anything opted out via data-no-prefetch. Touch devices prefetch on
  //    first tap instead of waiting for a hover that will never happen.
  // ---------------------------------------------------------------------
  var HOVER_DELAY = 650;
  var EXCLUDE_PATTERN = /\/(cart|checkout|account)(\/|$|\?)/i;
  var NON_DOC_EXT = /\.(pdf|zip|png|jpe?g|gif|svg|webp|mp4)$/i;
  var prefetched = new Set();
  var hoverTimer = null;

  function isSaveDataOrSlow() {
    var c = navigator.connection;
    if (!c) return false;
    return !!c.saveData || /2g/.test(c.effectiveType || "");
  }

  function prefetch(href) {
    if (prefetched.has(href) || prefetched.size > 10) return;
    prefetched.add(href);
    var link = document.createElement("link");
    link.rel = "prefetch";
    link.href = href;
    document.head.appendChild(link);
    if (DEBUG) console.info("[PerfEngine] prefetched:", href);
  }

  function eligibleLink(a) {
    if (!a || a.tagName !== "A" || !a.href) return null;
    if (a.hasAttribute("data-no-prefetch")) return null;
    if (a.target === "_blank") return null;
    var url;
    try { url = new URL(a.href, window.location.href); } catch (e) { return null; }
    if (url.origin !== window.location.origin) return null;
    if (EXCLUDE_PATTERN.test(url.pathname)) return null;
    if (NON_DOC_EXT.test(url.pathname)) return null;
    if (url.href === window.location.href) return null;
    return url.href;
  }

  function initHoverPreload() {
    if (isSaveDataOrSlow()) return;
    document.addEventListener("mouseover", function (e) {
      var a = e.target.closest ? e.target.closest("a") : null;
      var href = eligibleLink(a);
      if (!href) return;
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(function () { prefetch(href); }, HOVER_DELAY);
    }, { passive: true });

    document.addEventListener("mouseout", function () { clearTimeout(hoverTimer); }, { passive: true });

    document.addEventListener("touchstart", function (e) {
      var a = e.target.closest ? e.target.closest("a") : null;
      var href = eligibleLink(a);
      if (href) prefetch(href);
    }, { passive: true });
  }

  // ---------------------------------------------------------------------
  // 4. CLS reservation helper — strictly opt-in via data-cls-ratio="w/h".
  //    Never guesses at existing markup.
  // ---------------------------------------------------------------------
  function applyClsReservations(root) {
    var els = (root || document).querySelectorAll("[data-cls-ratio]:not([data-cls-applied])");
    els.forEach(function (el) {
      var parts = (el.getAttribute("data-cls-ratio") || "").split("/");
      if (parts.length === 2) {
        var w = parseFloat(parts[0]);
        var h = parseFloat(parts[1]);
        if (w > 0 && h > 0) {
          el.style.aspectRatio = w + " / " + h;
          el.setAttribute("data-cls-applied", "");
        }
      }
    });
  }

  // ---------------------------------------------------------------------
  // 5. Generic above-fold LCP preloader for lazysizes `data-bgset` elements.
  //    Runs once on load. Finds the largest visible-on-load element that
  //    carries data-bgset (the theme's actual responsive-image convention —
  //    see snippets/bgset.liquid), parses its candidate list, and preloads
  //    a reasonably-sized variant (capped at 2000px wide, to avoid pulling
  //    a multi-MB "master" original). Skips anything already preloaded
  //    (e.g. hero.liquid / image-slideshow.liquid, which already have a
  //    hand-placed preload from Phase 1/2 — this naturally de-dupes against
  //    those since they use the same img_url width buckets).
  //    Purely additive: never removes lazysizes' own data-bgset attribute
  //    or changes how/when the background-image itself actually applies.
  // ---------------------------------------------------------------------
  function autoPreloadAboveFoldBgImage() {
    var candidates = document.querySelectorAll("[data-bgset]");
    var best = null;
    var bestArea = 0;

    candidates.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      var visible = rect.top < window.innerHeight && rect.bottom > 0;
      if (!visible) return;
      var area = rect.width * rect.height;
      if (area > bestArea) {
        bestArea = area;
        best = el;
      }
    });

    if (!best) return;

    var raw = best.getAttribute("data-bgset") || "";
    var entries = [];
    var re = /(\S+)\s+(\d+)w\s+(\d+)h/g;
    var m;
    while ((m = re.exec(raw))) {
      entries.push({ url: m[1], w: parseInt(m[2], 10) });
    }
    if (!entries.length) return;

    entries.sort(function (a, b) { return a.w - b.w; });
    var pick = null;
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].w <= 2000) pick = entries[i];
    }
    if (!pick) pick = entries[0];

    if (document.querySelector('link[rel="preload"][href="' + pick.url + '"]')) return;

    var link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.setAttribute("fetchpriority", "high");
    link.href = pick.url;
    document.head.appendChild(link);
    if (DEBUG) console.info("[PerfEngine] auto-preloaded above-fold image:", pick.url);
  }

  // ---------------------------------------------------------------------
  // 6. Lightweight local metrics (no backend — call PerfEngine.metrics()
  //    in devtools to inspect; nothing is sent anywhere).
  // ---------------------------------------------------------------------
  function initMetrics() {
    if (!("PerformanceObserver" in window)) return;

    var nav = performance.getEntriesByType && performance.getEntriesByType("navigation")[0];
    if (nav) PerfEngine._metrics.ttfb = Math.round(nav.responseStart - nav.requestStart);

    try {
      new PerformanceObserver(function (list) {
        var entries = list.getEntries();
        var last = entries[entries.length - 1];
        PerfEngine._metrics.lcp = Math.round(last.startTime);
      }).observe({ type: "largest-contentful-paint", buffered: true });
    } catch (e) {}

    try {
      var clsValue = 0, sessionEntries = [], sessionValue = 0;
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (entry) {
          if (entry.hadRecentInput) return;
          var first = sessionEntries[0];
          var lastE = sessionEntries[sessionEntries.length - 1];
          if (sessionValue && entry.startTime - lastE.startTime < 1000 && entry.startTime - first.startTime < 5000) {
            sessionValue += entry.value;
            sessionEntries.push(entry);
          } else {
            sessionValue = entry.value;
            sessionEntries = [entry];
          }
          if (sessionValue > clsValue) {
            clsValue = sessionValue;
            PerfEngine._metrics.cls = parseFloat(clsValue.toFixed(4));
          }
        });
      }).observe({ type: "layout-shift", buffered: true });
    } catch (e) {}
  }

  PerfEngine.metrics = function () {
    console.table(PerfEngine._metrics);
    return PerfEngine._metrics;
  };

  // ---------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------
  applyClsReservations(document);
  new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].addedNodes.length) {
        applyClsReservations(document);
        break;
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  function boot() {
    initHoverPreload();
    autoPreloadAboveFoldBgImage();
    initMetrics();
    if (DEBUG) {
      console.info("[PerfEngine] ready — PerfEngine.report() for integrations, PerfEngine.metrics() for LCP/CLS/TTFB, PerfEngine.loadScript(src, {tier}) for deferred loading.");
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
